import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import type { ShopWithDetails } from '../../services/admin.service';
import { adminService } from '../../services/admin.service';

// ─── Toast ────────────────────────────────────────────────────────────────────

interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}

function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  function showToast(type: 'success' | 'error', message: string) {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }

  return { toasts, showToast };
}

function ToastContainer({ toasts }: { toasts: Toast[] }) {
  if (toasts.length === 0) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg ${
            t.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          }`}
        >
          {t.type === 'success' ? (
            <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )}
          {t.message}
        </div>
      ))}
    </div>
  );
}

// ─── Toggle switch ────────────────────────────────────────────────────────────

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6366F1] focus-visible:ring-offset-1 disabled:opacity-50 ${
        checked ? 'bg-[#6366F1]' : 'bg-gray-200'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// ─── Rush mode badge ──────────────────────────────────────────────────────────

function RushBadge({ mode }: { mode: string }) {
  const styles: Record<string, string> = {
    normal: 'bg-gray-100 text-gray-600',
    busy: 'bg-amber-100 text-amber-700',
    heavy_rush: 'bg-red-100 text-red-600',
  };
  const labels: Record<string, string> = {
    normal: 'Normal',
    busy: 'Busy',
    heavy_rush: 'Heavy Rush',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${styles[mode] ?? 'bg-gray-100 text-gray-600'}`}
    >
      {labels[mode] ?? mode}
    </span>
  );
}

// ─── Subscription badge ───────────────────────────────────────────────────────

function SubBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    trial: 'bg-blue-100 text-blue-700',
    expired: 'bg-red-100 text-red-600',
    grace: 'bg-amber-100 text-amber-700',
    cancelled: 'bg-gray-100 text-gray-500',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${styles[status] ?? 'bg-gray-100 text-gray-600'}`}
    >
      {status}
    </span>
  );
}

// ─── Table skeleton ───────────────────────────────────────────────────────────

function RowSkeleton() {
  return (
    <tr>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <td key={i} className="px-4 py-4">
          <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
        </td>
      ))}
    </tr>
  );
}

// ─── Add shop slide-over form ─────────────────────────────────────────────────

interface AddShopPanelProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
  onShopAdded: (shop: ShopWithDetails) => void;
}

interface FormState {
  shopName: string;
  location: string;
  ownerName: string;
  ownerEmail: string;
  ownerPassword: string;
}

interface FormErrors {
  shopName?: string;
  location?: string;
  ownerName?: string;
  ownerEmail?: string;
  ownerPassword?: string;
}

function AddShopPanel({ open, onClose, onSuccess, onError, onShopAdded }: AddShopPanelProps) {
  const [form, setForm] = useState<FormState>({
    shopName: '',
    location: '',
    ownerName: '',
    ownerEmail: '',
    ownerPassword: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => firstInputRef.current?.focus(), 50);
    } else {
      setForm({ shopName: '', location: '', ownerName: '', ownerEmail: '', ownerPassword: '' });
      setErrors({});
    }
  }, [open]);

  function validate(): boolean {
    const e: FormErrors = {};
    if (!form.shopName.trim()) e.shopName = 'Shop name is required';
    if (!form.location.trim()) e.location = 'Location is required';
    if (!form.ownerName.trim()) e.ownerName = 'Owner name is required';
    if (!form.ownerEmail.trim()) e.ownerEmail = 'Owner email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.ownerEmail))
      e.ownerEmail = 'Enter a valid email';
    if (!form.ownerPassword) e.ownerPassword = 'Password is required';
    else if (form.ownerPassword.length < 8) e.ownerPassword = 'Min 8 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const result = await adminService.createShop({
        shopName: form.shopName,
        location: form.location,
        ownerName: form.ownerName,
        ownerEmail: form.ownerEmail,
        ownerPassword: form.ownerPassword,
      });
      onShopAdded({
        id: result.shop.id,
        name: result.shop.name,
        location: result.shop.location,
        ownerName: result.owner.name,
        ownerEmail: result.owner.email,
        rushMode: result.shop.rushMode,
        isActive: result.shop.isActive,
        createdAt: String(result.shop.createdAt),
        subscription: null,
      });
      onSuccess('Shop created successfully');
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to create shop');
    } finally {
      setSubmitting(false);
    }
  }

  function field(
    id: keyof FormState,
    label: string,
    type = 'text',
    ref?: React.RefObject<HTMLInputElement>,
  ) {
    return (
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-[#0F172A]">
          {label}
        </label>
        <input
          ref={ref}
          id={id}
          type={type}
          autoComplete="off"
          className={`mt-1 block w-full rounded-lg border px-3 py-2 text-sm text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#6366F1] ${
            errors[id] ? 'border-red-400' : 'border-gray-200'
          }`}
          value={form[id]}
          onChange={(ev) => {
            setForm((prev) => ({ ...prev, [id]: ev.target.value }));
            if (errors[id]) setErrors((prev) => ({ ...prev, [id]: undefined }));
          }}
        />
        {errors[id] && <p className="mt-1 text-xs text-red-500">{errors[id]}</p>}
      </div>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/30 transition-opacity duration-300 ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-white shadow-2xl transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-[#0F172A]">Add New Shop</h2>
          <button
            onClick={onClose}
            className="text-[#64748B] hover:text-[#0F172A]"
            aria-label="Close panel"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" strokeLinecap="round" />
              <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <form
          onSubmit={(e) => void handleSubmit(e)}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-5 px-6 py-6">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#64748B]">
              Shop details
            </p>
            {field('shopName', 'Shop Name', 'text', firstInputRef)}
            {field('location', 'Location')}
            <p className="pt-2 text-sm font-semibold uppercase tracking-wider text-[#64748B]">
              Owner account
            </p>
            {field('ownerName', 'Owner Name')}
            {field('ownerEmail', 'Owner Email', 'email')}
            {field('ownerPassword', 'Password', 'password')}
          </div>

          <div className="border-t border-gray-200 px-6 py-4 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-[#64748B] hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-lg bg-[#6366F1] px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600 disabled:opacity-60 transition-colors"
            >
              {submitting ? 'Creating…' : 'Create Shop'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminShopsPage() {
  const [shops, setShops] = useState<ShopWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);
  const { toasts, showToast } = useToast();

  useEffect(() => {
    void loadShops();
  }, []);

  async function loadShops() {
    setLoading(true);
    try {
      const data = await adminService.getShops();
      setShops(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shops');
    } finally {
      setLoading(false);
    }
  }

  async function handleToggle(shopId: string) {
    setToggling(shopId);
    try {
      const updated = await adminService.toggleShopActive(shopId);
      setShops((prev) =>
        prev.map((s) => (s.id === shopId ? { ...s, isActive: updated.isActive } : s)),
      );
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Toggle failed');
    } finally {
      setToggling(null);
    }
  }

  function handleShopAdded(shop: ShopWithDetails) {
    setShops((prev) => [shop, ...prev]);
  }

  const filtered = shops.filter(
    (s) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.location.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div className="relative flex-1 max-w-xs">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="search"
              placeholder="Search shops…"
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-4 text-sm text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setPanelOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-[#6366F1] px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600 transition-colors"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" strokeLinecap="round" />
              <line x1="5" y1="12" x2="19" y2="12" strokeLinecap="round" />
            </svg>
            Add Shop
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-[#F8FAFC]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                    Shop
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                    Owner
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                    Subscription
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                    Rush Mode
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                    Active
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <>
                    <RowSkeleton />
                    <RowSkeleton />
                    <RowSkeleton />
                    <RowSkeleton />
                    <RowSkeleton />
                  </>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-[#64748B]">
                      {search ? `No shops matching "${search}"` : 'No shops yet'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((shop) => (
                    <tr key={shop.id} className="hover:bg-[#F8FAFC] transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-[#0F172A]">{shop.name}</p>
                        <p className="text-xs text-[#64748B]">{shop.location}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-[#0F172A]">{shop.ownerName}</p>
                        <p className="text-xs text-[#64748B]">{shop.ownerEmail}</p>
                      </td>
                      <td className="px-4 py-3">
                        {shop.subscription ? (
                          <div className="space-y-1">
                            <p className="text-xs font-medium uppercase text-[#64748B]">
                              {shop.subscription.planName}
                            </p>
                            <SubBadge status={shop.subscription.status} />
                          </div>
                        ) : (
                          <span className="text-xs text-[#94A3B8]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <RushBadge mode={shop.rushMode} />
                      </td>
                      <td className="px-4 py-3">
                        <Toggle
                          checked={shop.isActive}
                          onChange={() => void handleToggle(shop.id)}
                          disabled={toggling === shop.id}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/shops/${shop.id}`}
                          className="text-xs font-medium text-[#6366F1] hover:text-indigo-700"
                        >
                          View details
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AddShopPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        onSuccess={(msg) => showToast('success', msg)}
        onError={(msg) => showToast('error', msg)}
        onShopAdded={handleShopAdded}
      />

      <ToastContainer toasts={toasts} />
    </>
  );
}
